import dayjs from "dayjs";

// Hooks
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import useLogin from "./useLogin";
import useSessionModal from "./useSessionModal";

// Helpers
import { handleError } from "../helpers/error";
import { DATE_FORMAT, TIME_FORMAT } from "@common/constants/datetime";
import { fetchInteractionOptions } from "../helpers/CustomerInteraction/fetchInteractionOptions";

// APIs
import ComplianceAlertClient from "@common/api/ComplianceAlertClient";
import AlertConfigurationClient from "@common/api/AlertConfigurationClient";
import CustomerClient from "@common/api/CustomerClient";

// Actions
import LoadingAction from "@common/redux/actions/LoadingAction";
import LinkAlertAction from "@common/redux/actions/LinkAlertAction";
import SessionalDetailsAction from "@common/redux/actions/SessionalDetailsAction";
import SuspiciousActivityAction from "@common/redux/actions/SuspiciousActivityAction";
import CustomerInteractionAction from "@common/redux/actions/CustomerInteractionAction";
import CustomerFormAction from "@common/redux/actions/CustomerFormAction";
import ObservationsAction from "@common/redux/actions/ObservationsAction";
import ConfirmModalAction from "@common/redux/actions/ConfirmModalAction";
import CustomerIncidentAction from "@common/redux/actions/CustomerIncidentAction";
import { mapCustomersToOptions } from "../helpers/Customer/customers";

const useLinkedAlert = () => {
  const history = useHistory();
  const data = useSelector((state) => LinkAlertAction.getStatePath(state));
  const setState = LinkAlertAction.dispatch;
  const setSuspiciousActivityState = SuspiciousActivityAction.dispatch;
  const setObservationsState = ObservationsAction.dispatch;
  const setSessionalDetailsState = SessionalDetailsAction.dispatch;
  const setCustomerInteractionState = (data) => CustomerInteractionAction.dispatch(data);
  const setCustomerIncidentsState = (data) => CustomerIncidentAction.dispatch(data);

  const [{ site }] = useLogin();

  const { fetchComplianceSessions } = useSessionModal();

  const details = useSelector((state) =>
    SessionalDetailsAction.getStatePath(state),
  );

  const toggleAlert = (alertId, selectedAlerts) => {
    if (!selectedAlerts) return;
    const updatedAlerts = selectedAlerts?.includes(alertId)
      ? selectedAlerts.filter((x) => x !== alertId)
      : [...selectedAlerts, alertId];
    setState({ selectedAlerts: updatedAlerts });
  };

  const toggleAllAlerts = (alerts, selectedAlerts) => {
    if (!alerts || alerts.length === 0 || !selectedAlerts) return;
    if (selectedAlerts.length > 0) setState({ selectedAlerts: [] });
    else setState({ selectedAlerts: alerts.map((x) => x.id) });
  };

  const confirmRemoveAlerts = (selectedAlerts, linkedAlertData) => {
    if (!selectedAlerts || !linkedAlertData) return;
    ConfirmModalAction.open(
      "This is the triggered alert, are you sure you want to remove this?",
      "Confirm remove alerts",
      () => removeSelectedAlerts(selectedAlerts),
    );
  };

  const removeSelectedAlerts = (selectedAlerts, linkedAlertData) => {
    if (!selectedAlerts || !linkedAlertData) return;
    const updatedAlerts = linkedAlertData.filter(
      (x) => !selectedAlerts.find((s) => s === x.id),
    );
    setState({ linkedAlertData: updatedAlerts });
  };

  const fetchAlertsByIds = (ids) => {
    LoadingAction.set(true).then(() =>
      ComplianceAlertClient.getAlerts({ ids, siteId: site?.id })
        .then((response) => {
          setState({ linkedAlertData: response.data });
        })
        .catch(handleError)
        .finally(() => {
          LoadingAction.set(false);
        }),
    );
  };

  const fetchAlertConfig = () => {
    return LoadingAction.set(true).then(() =>
      AlertConfigurationClient.getMetaData()
        .then((response) => {
          setState({ alertConfig: response });
          return response;
        })
        .catch(handleError)
        .finally(() => LoadingAction.set(false)),
    );
  };

  const redirectAlertToSuspiciousActivity = (alert) => {
    alert.machineInstallationId &&
      fetchComplianceSessions(
        alert.machineInstallationId,
        details.periodType,
        details.pageNumber,
      );
    setSuspiciousActivityState({
      complianceAlertId: alert.id,
      formData: {
        date: dayjs(alert.alertTime).format(DATE_FORMAT),
        time: dayjs(alert.alertTime).format(TIME_FORMAT),
        machine: { installationId: alert.machineInstallationId },
        machineReference: alert.machineInstallationNumber,
        customerIds: [],
        customers: [],
      },
    });
    setSessionalDetailsState({
      machineInstallationId: alert.machineInstallationId,
    });
    history.push({
      pathname: "/compliance/suspicious-activity",
      state: { type: "alert" },
    });
  };

  const redirectAlertToObservations = (alert) => {
    setObservationsState({
      complianceAlertId: alert.id,
      sessionIds: alert.sessionGuid ? [alert.sessionGuid] : [],
    });
    history.push({
      pathname: "/compliance/observations",
      state: { type: "alert" },
    });
  };

  const redirectAlertToCustomerInteraction = async (alert) => {
    let selectedCustomer = alert?.customerId ? await CustomerClient.Read(alert?.customerId) : null;
    // Preset vulnerable state and only hide customer search with a valid prefill.
    setCustomerInteractionState({
      isCustomerVulnerable: true,
      complianceAlertId: alert.id,
      hideCustomerSearch: selectedCustomer ? true : false,
      customers: selectedCustomer ? [selectedCustomer] : [],
      customerId: selectedCustomer?.id,
      searchedCustomer: selectedCustomer?.id,
    });

    CustomerFormAction.dispatch({
      customers: selectedCustomer ? mapCustomersToOptions([selectedCustomer]) : [],
    });

    fetchInteractionOptions();
    history.push({
      pathname: "/compliance/new-customer-interaction",
      state: { type: "alert" },
    });
  };

  const redirectAlertToCustomerIncidents = async (alert) => {
    let selectedCustomer = await CustomerClient.Read(alert?.customerId);
    // Preset vulnerable state and only hide customer search with a valid prefill.
    setCustomerIncidentsState({
      complianceAlertId: alert.id,
      customers: [selectedCustomer],
      customerId: selectedCustomer.id,
      searchedCustomer: selectedCustomer.id,
    });

    CustomerFormAction.dispatch({
      customers: mapCustomersToOptions([selectedCustomer]),
    });

    // fetchInteractionOptions();

    history.push({
      pathname: "/compliance/customer-incidents",
      state: { type: "alert" },
    });
  };

  return {
    ...data,
    setState,
    toggleAlert,
    toggleAllAlerts,
    confirmRemoveAlerts,
    removeSelectedAlerts,
    fetchAlertsByIds,
    fetchAlertConfig,
    redirectAlertToSuspiciousActivity,
    redirectAlertToObservations,
    redirectAlertToCustomerInteraction,
    redirectAlertToCustomerIncidents,
  };
};

export default useLinkedAlert;
